import { runInAction, set } from 'mobx';

import type { IImportColumn, IPreviewCleanedCell, IPreviewRow } from '@feathr/blackbox';
import { defaultPersonAttributes } from '@feathr/blackbox';

export const SAMPLE_DISPLAY_LENGTH = 60;
export const SAMPLE_TITLE_LENGTH = 200;

/*
 * Maps each saved column name to the first non-empty value in that column of
 * the preview's input rows. A column with no value maps to an empty string.
 * Input rows are keyed by the import's saved column names, so a headerless
 * file is keyed `Column 1` to `Column N`.
 */
export function getSampleValues(
  rows: IPreviewRow[],
  columnNames: string[],
): Record<string, string> {
  return Object.fromEntries(
    columnNames.map((columnName) => {
      const key = String(columnName);
      const sample = rows
        .map((row) => toCellText(row.input?.[key] as unknown))
        .find((text) => text.trim() !== '');
      return [key, sample ?? ''];
    }),
  );
}

/**
 * The distinct columns of one previewed row whose value the mapped field's type could not read.
 * A short row's dropped cells (`unreachable_column`) are the row's own problem, not the
 * mapping's, so they are left out.
 */
export function unreadableColumns(row: IPreviewRow): string[] {
  return [
    ...new Set(
      (row.problems ?? [])
        .filter((problem) => problem.reason === 'unparseable_cell')
        .map((problem) => problem.column_name),
    ),
  ];
}

/** Each column with an unreadable cell in the preview, against the first such value. */
export function getUnreadableValues(rows: IPreviewRow[]): Map<string, string> {
  const values = new Map<string, string>();
  for (const row of rows) {
    for (const columnName of unreadableColumns(row)) {
      // A column named `constructor` or `toString` would otherwise read off Object.prototype.
      const cell = row.input && Object.hasOwn(row.input, columnName) ? row.input[columnName] : '';
      const value = toCellText(cell as unknown);
      if (!values.has(columnName) && value.trim() !== '') {
        values.set(columnName, value);
      }
    }
  }
  return values;
}

/**
 * Fills `{{key}}` placeholders in already translated copy. Cell values go in here rather than
 * through i18next, which rescans what it interpolates (a cell reading `{{type}}` would come out as
 * the field's type). One pass with a function replacer keeps a value reading `$&` literal and a
 * value reading `{{value}}` from being filled again.
 */
export function fillPlaceholders(text: string, values: Record<string, string>): string {
  return text.replace(/\{\{(\w+)\}\}/g, (match, key: string) =>
    Object.hasOwn(values, key) ? values[key] : match,
  );
}

// Grouping off so 1000 reads as the number the field stores; String() would write 1e+21.
const cleanedValueFormat = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 20,
  useGrouping: false,
});

/** A cleaned cell's number as it will import, never in exponent form. */
export function formatCleanedValue(value: number): string {
  return cleanedValueFormat.format(value);
}

/** Each column with a cleaned cell in the preview, against the first such cell. */
export function getCleanedExamples(rows: IPreviewRow[]): Map<string, IPreviewCleanedCell> {
  const examples = new Map<string, IPreviewCleanedCell>();
  for (const row of rows) {
    for (const cell of row.cleaned ?? []) {
      if (!examples.has(cell.column_name)) {
        examples.set(cell.column_name, cell);
      }
    }
  }
  return examples;
}

// Input rows are typed as strings, but the preview can carry numbers, booleans or nested values.
function toCellText(value: unknown): string {
  if (value === undefined || value === null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return value.toString();
  }
  return JSON.stringify(value) ?? '';
}

export function truncateSample(value: string, maxLength: number): string {
  return value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value;
}

/*
 * Maps a column to the built-in field of the same id (e.g. a column named "email"), unless the
 * column already maps to something. Returns whether it mapped the column.
 */
export function matchBuiltInField(column: IImportColumn): boolean {
  if (column.feathr_attr) {
    return false;
  }
  const columnName = String(column.column_name).trim().toLowerCase();
  const attr = defaultPersonAttributes.find(({ id }) => id === columnName);
  if (!attr) {
    return false;
  }
  runInAction(() => {
    set(column, { feathr_attr: attr.id, attr_type: attr.data_type });
  });
  return true;
}

/** Everything in the mappings that the preview depends on, as a key that changes with any of it. */
export function getPreviewMappingKey(columnMappers: IImportColumn[]): string {
  return JSON.stringify(
    columnMappers.map((column) => [
      column.column_name,
      column.feathr_attr,
      column.attr_type,
      column.u_key,
    ]),
  );
}

/** The first column of the file that no mapping row uses yet. */
export function getNextUnmappedColumn(
  columnNames: string[],
  columnMappers: IImportColumn[],
): string | undefined {
  const used = new Set(columnMappers.map((column) => String(column.column_name)));
  return columnNames.find((columnName) => !used.has(String(columnName)));
}
